<div class="portfolio-portal-page">
  <app-portal-list-surface
    title="PORTFOLIO"
    subtitle="View portfolio companies for this fund">
    <app-portal-toolbar portalListHeader class="portfolio-header-actions">
      <div portalToolbarRight>
        <mat-form-field class="portfolio-search-field">
          <img src="assets/images/search.svg" alt="search" matPrefix />
          <input
            matInput
            placeholder="Search"
            [(ngModel)]="search"
            (input)="applyFilter()"
            autocomplete="off" />
          <button
            class="portfolio-search-field__clear"
            *ngIf="search"
            matSuffix
            type="button"
            (click)="clearFilter()">
            <img
              src="assets/images/cross_icon_grey.svg"
              height="20"
              width="20"
              alt="clear" />
          </button>
        </mat-form-field>

        <button
          *ngIf="showPortfolioManagementFlow"
          class="portal-primary-btn portfolio-add-company-btn"
          type="button"
          (click)="openManagement()">
          <mat-icon>tune</mat-icon>
          <span>Manage data</span>
        </button>
      </div>
    </app-portal-toolbar>

    <section class="portfolio-overview" *ngIf="portfolioOverview as overview">
      <div class="portfolio-summary-grid">
        <article class="portfolio-summary-card">
          <span class="portfolio-summary-card__label">Companies</span>
          <strong>{{ overview.summary.companyCount }}</strong>
          <span>{{ currencyContextLabel }}</span>
        </article>
        <article class="portfolio-summary-card">
          <span class="portfolio-summary-card__label">Latest period</span>
          <strong>{{ latestPeriodLabel || '-' }}</strong>
          <span>{{ latestTrendCoverageLabel }}</span>
        </article>
        <article class="portfolio-summary-card">
          <span class="portfolio-summary-card__label">Latest revenue</span>
          <strong>{{ formatCurrencyValue(latestRevenue) }}</strong>
          <span>{{ trendTypeLabel(selectedTrendType) }} · {{ trendMode === 'comparable' ? 'comparable cohort' : 'all reported' }}</span>
        </article>
        <article
          class="portfolio-summary-card"
          [class.portfolio-summary-card--negative]="(latestEbitda || 0) < 0">
          <span class="portfolio-summary-card__label">Latest EBITDA</span>
          <strong>{{ formatCurrencyValue(latestEbitda) }}</strong>
          <span>{{ trendTypeLabel(selectedTrendType) }} · {{ trendMode === 'comparable' ? 'comparable cohort' : 'all reported' }}</span>
        </article>
        <article class="portfolio-summary-card">
          <span class="portfolio-summary-card__label">Fair value</span>
          <strong>{{ formatCurrencyValue(fairValueTotal) }}</strong>
          <span>{{ fairValueCoverageLabel }} · MOIC {{ formatMoic(selectedImpliedMoic) }}</span>
        </article>
        <article class="portfolio-summary-card">
          <span class="portfolio-summary-card__label">Reporting coverage</span>
          <strong>{{ reportingCoverageLabel }}</strong>
          <span>Companies complete</span>
        </article>
      </div>

      <div class="portfolio-dashboard-grid">
        <section class="portfolio-panel portfolio-trend-panel">
          <div class="portfolio-panel__header">
            <div>
              <h2>Revenue vs EBITDA Trend</h2>
              <p>{{ trendContextDescription }}</p>
            </div>
            <div class="portfolio-chart-context" *ngIf="trendData.length || availableCurrencies.length > 1">
              <div class="portfolio-chart-switch" *ngIf="availableCurrencies.length > 1" aria-label="Portfolio currency">
                <button
                  *ngFor="let currency of availableCurrencies"
                  type="button"
                  [class.portfolio-chart-switch__button--active]="selectedCurrency === currency"
                  [attr.aria-pressed]="selectedCurrency === currency"
                  (click)="setCurrency(currency)">
                  {{ currency }}
                </button>
              </div>
              <div class="portfolio-chart-switch" *ngIf="trendTypes.length > 1" aria-label="Reporting period type">
                <button
                  *ngFor="let type of trendTypes"
                  type="button"
                  [class.portfolio-chart-switch__button--active]="selectedTrendType === type"
                  [attr.aria-pressed]="selectedTrendType === type"
                  (click)="setTrendType(type)">
                  {{ trendTypeLabel(type) }}
                </button>
              </div>
              <div class="portfolio-chart-switch" aria-label="Reporting cohort">
                <button
                  type="button"
                  [disabled]="!hasComparableTrend"
                  [class.portfolio-chart-switch__button--active]="trendMode === 'comparable'"
                  [attr.aria-pressed]="trendMode === 'comparable'"
                  (click)="setTrendMode('comparable')">
                  Comparable cohort
                </button>
                <button
                  type="button"
                  [class.portfolio-chart-switch__button--active]="trendMode === 'all_reported'"
                  [attr.aria-pressed]="trendMode === 'all_reported'"
                  (click)="setTrendMode('all_reported')">
                  All reported
                </button>
              </div>
              <div class="portfolio-chart-legend">
                <span><i class="portfolio-chart-legend__dot portfolio-chart-legend__dot--revenue"></i>Revenue</span>
                <span><i class="portfolio-chart-legend__dot portfolio-chart-legend__dot--ebitda"></i>EBITDA</span>
              </div>
            </div>
          </div>

          <div class="portfolio-chart" *ngIf="trendData.length; else emptyTrend">
            <div class="portfolio-chart__y-axis">
              <span
                *ngFor="let tick of chartGridValues"
                [style.top.%]="trendTickOffset(tick)">
                {{ formatCurrencyValue(tick, primaryCurrency, true) }}
              </span>
            </div>

            <div class="portfolio-chart__plot">
              <svg
                [attr.viewBox]="'0 0 ' + chartInnerWidth + ' ' + chartInnerHeight"
                preserveAspectRatio="none"
                aria-hidden="true">
                <line
                  *ngFor="let tick of chartGridValues"
                  class="portfolio-chart__grid-line"
                  x1="0"
                  [attr.x2]="chartInnerWidth"
                  [attr.y1]="trendPlotY(tick)"
                  [attr.y2]="trendPlotY(tick)" />
                <line
                  class="portfolio-chart__axis"
                  x1="0"
                  [attr.x2]="chartInnerWidth"
                  [attr.y1]="trendPlotY(0)"
                  [attr.y2]="trendPlotY(0)" />
                <path class="portfolio-chart__line portfolio-chart__line--revenue" [attr.d]="trendPlotPath('revenue')" />
                <path class="portfolio-chart__line portfolio-chart__line--ebitda" [attr.d]="trendPlotPath('ebitda')" />
                <line
                  *ngFor="let point of trendPlotPoints('revenue')"
                  class="portfolio-chart__point portfolio-chart__point--revenue"
                  [attr.x1]="point.x"
                  [attr.x2]="point.x"
                  [attr.y1]="point.y"
                  [attr.y2]="point.y" />
                <line
                  *ngFor="let point of trendPlotPoints('ebitda')"
                  class="portfolio-chart__point portfolio-chart__point--ebitda"
                  [attr.x1]="point.x"
                  [attr.x2]="point.x"
                  [attr.y1]="point.y"
                  [attr.y2]="point.y" />
              </svg>
            </div>

            <div class="portfolio-chart__x-axis">
              <span
                *ngFor="let point of trendData; let i = index"
                [style.left.%]="trendLabelOffset(i)">
                <b>{{ point.periodLabel }}</b>
                <small>{{ trendPointCoverage(point) }}</small>
              </span>
            </div>
          </div>
          <ng-template #emptyTrend>
            <div class="portfolio-empty-panel">No revenue or EBITDA data has been reported yet.</div>
          </ng-template>
        </section>

        <section class="portfolio-panel portfolio-attention-panel">
          <div class="portfolio-panel__header">
            <div>
              <h2>Needs attention</h2>
              <p>Signals from reporting, profile setup, and latest metrics.</p>
            </div>
            <span class="portfolio-attention-count">{{ overview.attentionItems.length }}</span>
          </div>

          <div class="portfolio-attention-list" *ngIf="visibleAttentionItems.length; else allClear">
            <button
              *ngFor="let item of visibleAttentionItems"
              type="button"
              class="portfolio-attention-item"
              [ngClass]="attentionSeverityClass(item)"
              (click)="navigateToPortfolioCompanyById(item.companyId)">
              <span class="portfolio-attention-item__severity"></span>
              <span>
                <strong>{{ item.title }}</strong>
                <small>{{ item.description }}</small>
              </span>
            </button>
          </div>
          <ng-template #allClear>
            <div class="portfolio-empty-panel">No portfolio companies need attention right now.</div>
          </ng-template>
        </section>
      </div>

      <section class="portfolio-insight-grid">
        <article
          class="portfolio-panel portfolio-value-panel"
          [class.portfolio-panel--collapsed]="isOverviewSectionCollapsed('valueCreation')">
          <div class="portfolio-panel__header">
            <div>
              <h2>Value creation</h2>
              <p>Latest available valuation inputs per company · {{ fairValueCoverageLabel }}.</p>
            </div>
            <div class="portfolio-panel__header-actions">
              <strong>{{ formatMoic(overview.summary.impliedMoic) }}</strong>
              <button
                type="button"
                class="portfolio-panel-toggle"
                [class.portfolio-panel-toggle--collapsed]="isOverviewSectionCollapsed('valueCreation')"
                [attr.aria-expanded]="!isOverviewSectionCollapsed('valueCreation')"
                aria-label="Toggle value creation details"
                (click)="toggleOverviewSection('valueCreation')">
                <mat-icon>expand_more</mat-icon>
              </button>
            </div>
          </div>

          <div class="portfolio-value-steps" *ngIf="!isOverviewSectionCollapsed('valueCreation')">
            <div
              class="portfolio-value-step"
              *ngFor="let step of valueCreationSteps"
              [ngClass]="valueStepClass(step)">
              <div class="portfolio-value-step__header">
                <span>{{ step.label }}</span>
                <strong>{{ step.value }}</strong>
              </div>
              <div class="portfolio-value-step__track">
                <i [style.width.%]="step.percent"></i>
              </div>
              <small>{{ step.detail }}</small>
            </div>
          </div>
        </article>

        <article
          class="portfolio-panel portfolio-health-panel"
          [class.portfolio-panel--collapsed]="isOverviewSectionCollapsed('reportingHealth')">
          <div class="portfolio-panel__header">
            <div>
              <h2>Reporting health</h2>
              <p>Latest available reporting period per company.</p>
            </div>
            <div class="portfolio-panel__header-actions">
              <strong>{{ reportingCoverageLabel }}</strong>
              <button
                type="button"
                class="portfolio-panel-toggle"
                [class.portfolio-panel-toggle--collapsed]="isOverviewSectionCollapsed('reportingHealth')"
                [attr.aria-expanded]="!isOverviewSectionCollapsed('reportingHealth')"
                (click)="toggleOverviewSection('reportingHealth')">
                <mat-icon>expand_more</mat-icon>
              </button>
            </div>
          </div>

          <div class="portfolio-collapsible-body" *ngIf="!isOverviewSectionCollapsed('reportingHealth')">
            <div class="portfolio-health-bar">
              <span
                *ngFor="let item of reportingStatusBreakdown"
                [ngClass]="reportingBreakdownClass(item)"
                [style.width.%]="item.percent">
              </span>
            </div>

            <div class="portfolio-health-list">
              <div
                class="portfolio-health-item"
                *ngFor="let item of reportingStatusBreakdown"
                [ngClass]="reportingBreakdownClass(item)">
                <span>{{ item.label }}</span>
                <strong>{{ item.count }}</strong>
              </div>
            </div>
          </div>
        </article>

        <article
          class="portfolio-panel portfolio-watchlist-panel"
          [class.portfolio-panel--collapsed]="isOverviewSectionCollapsed('runwayWatchlist')">
          <div class="portfolio-panel__header">
            <div>
              <h2>Runway watchlist</h2>
              <p>Cash and runway signals that may need follow-up.</p>
            </div>
            <div class="portfolio-panel__header-actions">
              <strong>{{ runwayWatchlist.length }}</strong>
              <button
                type="button"
                class="portfolio-panel-toggle"
                [class.portfolio-panel-toggle--collapsed]="isOverviewSectionCollapsed('runwayWatchlist')"
                [attr.aria-expanded]="!isOverviewSectionCollapsed('runwayWatchlist')"
                aria-label="Toggle runway watchlist"
                (click)="toggleOverviewSection('runwayWatchlist')">
                <mat-icon>expand_more</mat-icon>
              </button>
            </div>
          </div>

          <ng-container *ngIf="!isOverviewSectionCollapsed('runwayWatchlist')">
            <div class="portfolio-watchlist" *ngIf="runwayWatchlist.length; else noRunwaySignals">
              <button
                type="button"
                class="portfolio-watchlist-item"
                *ngFor="let item of runwayWatchlist"
                [ngClass]="watchlistSeverityClass(item)"
                (click)="navigateToPortfolioCompanyById(item.companyId)">
                <span class="portfolio-watchlist-item__severity"></span>
                <span class="portfolio-watchlist-item__content">
                  <strong>{{ item.companyName }}</strong>
                  <small>{{ item.detail }}</small>
                </span>
                <b>{{ item.value }}</b>
              </button>
            </div>
            <ng-template #noRunwaySignals>
              <div class="portfolio-empty-panel">No cash or runway watchlist signals right now.</div>
            </ng-template>
          </ng-container>
        </article>

        <article
          class="portfolio-panel portfolio-performance-panel"
          [class.portfolio-panel--collapsed]="isOverviewSectionCollapsed('performanceSignals')">
          <div class="portfolio-panel__header">
            <div>
              <h2>Performance signals</h2>
              <p>Quick leaders and laggards from the latest available data.</p>
            </div>
            <div class="portfolio-panel__header-actions">
              <strong>{{ performanceSignals.length }}</strong>
              <button
                type="button"
                class="portfolio-panel-toggle"
                [class.portfolio-panel-toggle--collapsed]="isOverviewSectionCollapsed('performanceSignals')"
                [attr.aria-expanded]="!isOverviewSectionCollapsed('performanceSignals')"
                aria-label="Toggle performance signals"
                (click)="toggleOverviewSection('performanceSignals')">
                <mat-icon>expand_more</mat-icon>
              </button>
            </div>
          </div>

          <ng-container *ngIf="!isOverviewSectionCollapsed('performanceSignals')">
            <div class="portfolio-performance-list" *ngIf="performanceSignals.length; else noPerformanceSignals">
              <button
                type="button"
                class="portfolio-performance-card"
                *ngFor="let item of performanceSignals"
                [ngClass]="performanceSignalClass(item)"
                (click)="item.companyId && navigateToPortfolioCompanyById(item.companyId)">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <small>{{ item.companyName }} · {{ item.detail }}</small>
              </button>
            </div>
            <ng-template #noPerformanceSignals>
              <div class="portfolio-empty-panel">Performance signals will appear once companies report data.</div>
            </ng-template>
          </ng-container>
        </article>
      </section>

      <section class="portfolio-panel portfolio-fair-value-panel">
        <div class="portfolio-panel__header">
          <div>
            <h2>Fair Value by Company</h2>
            <p>
              Latest FV of Holding by portfolio company.
              <span>{{ fairValueCoverageLabel }}.</span>
              <span *ngIf="topOneExposurePercent !== null">
                Top 1: {{ formatPercentValue(topOneExposurePercent) }}
              </span>
              <span *ngIf="topThreeExposurePercent !== null">
                Top 3: {{ formatPercentValue(topThreeExposurePercent) }}
              </span>
            </p>
          </div>
          <strong>{{ formatCurrencyValue(fairValueTotal) }}</strong>
        </div>

        <div class="portfolio-fair-value-list" *ngIf="fairValueRows.length; else emptyFairValue">
          <button
            *ngFor="let item of fairValueRows"
            type="button"
            class="portfolio-fair-value-row"
            (click)="navigateToPortfolioCompanyById(item.companyId)">
            <span class="portfolio-fair-value-row__label">
              <strong>{{ item.companyName }}</strong>
              <small>{{ item.latestPeriodLabel || 'Latest available' }}<ng-container *ngIf="item.latestPeriodEndDate"> · as of {{ item.latestPeriodEndDate | date:'mediumDate':'UTC' }}</ng-container></small>
            </span>
            <span class="portfolio-fair-value-row__track">
              <span
                class="portfolio-fair-value-row__bar"
                [style.width.%]="fairValuePercent(item)">
              </span>
            </span>
            <span class="portfolio-fair-value-row__value">
              <strong>{{ formatCurrencyValue(item.value, item.currency) }}</strong>
              <small>{{ formatPercentValue(fairValueShare(item)) }} of FV</small>
            </span>
          </button>
        </div>
        <ng-template #emptyFairValue>
          <div class="portfolio-empty-panel">No fair value data has been reported yet.</div>
        </ng-template>
      </section>
    </section>

    <app-portal-table
      [columns]="portfolioColumns"
      [rows]="tableRows()"
      [loading]="isLoading"
      [sortActive]="sortActive"
      [sortDirection]="portalSortDirection"
      emptyTitle="No portfolio companies found"
      emptyDescription="Portfolio companies will appear here once data is available."
      (sortChange)="portalSortChange($event)"
      (rowClick)="navigateToPortfolioCompany($event)">
      <ng-template portalTableCell="company" let-row>
        <span class="portfolio-company-cell">
          <span class="portfolio-company-cell__logo">
            <img
              *ngIf="!row.logo"
              src="assets/images/pc_icon.svg"
              alt=""
              height="22"
              width="22" />
            <img
              class="portfolio-company-cell__image"
              *ngIf="row.logo"
              [src]="row.logo"
              alt=""
              height="42"
              width="42" />
          </span>
          <span class="portfolio-company-cell__content">
            <span class="portfolio-company-cell__name">{{ row.name || '-' }}</span>
            <span class="portfolio-company-cell__meta" *ngIf="companyMeta(row)">
              {{ companyMeta(row) }}
            </span>
          </span>
        </span>
      </ng-template>

      <ng-template portalTableCell="latestPeriodLabel" let-row>
        <span class="portfolio-muted">
          {{ row.latestPeriodLabel || '-' }}
        </span>
      </ng-template>

      <ng-template portalTableCell="latestRevenue" let-row>
        <span class="portfolio-table-number">
          {{ formatCurrencyValue(row.latestRevenue, row.currency || primaryCurrency) }}
        </span>
      </ng-template>

      <ng-template portalTableCell="latestEbitda" let-row>
        <span
          class="portfolio-table-number"
          [class.portfolio-table-number--negative]="(row.latestEbitda || 0) < 0">
          {{ formatCurrencyValue(row.latestEbitda, row.currency || primaryCurrency) }}
        </span>
      </ng-template>

      <ng-template portalTableCell="fairValueOfHolding" let-row>
        <span class="portfolio-table-number">
          {{ formatCurrencyValue(row.fairValueOfHolding, row.currency || primaryCurrency) }}
        </span>
      </ng-template>

      <ng-template portalTableCell="moic" let-row>
        <span class="portfolio-table-number">
          {{ formatMoic(row.moic) }}
        </span>
      </ng-template>

      <ng-template portalTableCell="reportingStatus" let-row>
        <span class="portfolio-status" [ngClass]="reportingStatusClass(row.reportingStatus)">
          <span>{{ reportingStatusLabel(row.reportingStatus) }}</span>
          <small>{{ row.reportingCompletion || 0 }}%</small>
        </span>
      </ng-template>

      <app-portal-table-footer
        portalTableFooter
        [total]="totalRows()"
        [pageIndex]="pageIndex"
        [pageSize]="pageSize"
        [visibleCount]="tableRows().length"
        [pageSizeOptions]="pageSizeOptions"
        (pageIndexChange)="setPageIndex($event)"
        (pageSizeChange)="setRowsPerPage($event)">
      </app-portal-table-footer>
    </app-portal-table>
  </app-portal-list-surface>
</div>
